'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Button } from '@/components/Buttons';
import { CopyTextClient } from '@/components/CopyText/CopyTextClient';
import { DONATION_COMMENT_MAX_LENGTH, DONATION_CURRENCIES, DonationCurrency } from '@/constants/donations';
import { Global } from '@/types/sanity.types';

export interface DonationTranslations {
  methodLabel: string;
  payOnline: string;
  bankTransfer: string;
  transfer: {
    beneficiary: string;
    address: string;
    postalCode: string;
    bank: string;
    note: string;
  };
  frequencyLabel: string;
  currencyLabel: string;
  once: string;
  monthly: string;
  amountLabel: string;
  customAmount: string;
  commentLabel: string;
  commentNote: string;
  donateButton: string;
  redirectNote: string;
  monthlyNote: string;
  successMessage: string;
  cancelledMessage: string;
  errorMessage: string;
  receiptLink: string;
}

const TRANSFER_IBAN = 'RO75INGB0000999919303337';
const TRANSFER_IBAN_DISPLAY = 'RO75 INGB 0000 9999 1930 3337';

const presetButtonCSS = (selected: boolean) =>
  classNames(
    'rounded-md px-3.5 py-2.5 text-sm font-semibold shadow-sm transition-all',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600',
    selected
      ? 'bg-background-500 text-white dark:bg-background-600'
      : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-transparent dark:text-white dark:ring-white/20 dark:hover:bg-white/10',
  );

export const DonationForm = ({ translations: t, global }: { translations: DonationTranslations; global: Global }) => {
  const searchParams = useSearchParams();
  const status = searchParams?.get('status');
  const sessionId = searchParams?.get('session_id');

  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [method, setMethod] = useState<'online' | 'transfer'>(() =>
    searchParams?.get('method') === 'transfer' ? 'transfer' : 'online',
  );
  const [frequency, setFrequency] = useState<'once' | 'monthly'>('once');
  const [currency, setCurrency] = useState<DonationCurrency>('ron');
  const [selectedPreset, setSelectedPreset] = useState<number | 'custom'>(DONATION_CURRENCIES.ron.presets[1]);
  const [customAmount, setCustomAmount] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currencyConfig = DONATION_CURRENCIES[currency];

  const handleMethodChange = (value: 'online' | 'transfer') => {
    setMethod(value);
    const params = new URLSearchParams(window.location.search);
    if (value === 'online') {
      params.delete('method');
    } else {
      params.set('method', value);
    }
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  };

  const handleCurrencyChange = (value: DonationCurrency) => {
    setCurrency(value);
    setSelectedPreset(DONATION_CURRENCIES[value].presets[1]);
    setCustomAmount('');
  };

  useEffect(() => {
    if (status !== 'success' || !sessionId) return;
    fetch(`/api/donation-receipt?session_id=${encodeURIComponent(sessionId)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => setReceiptUrl(data?.receiptUrl ?? null))
      .catch(() => {});
  }, [status, sessionId]);

  const amount = selectedPreset === 'custom' ? parseInt(customAmount, 10) : selectedPreset;
  const amountIsValid = Number.isInteger(amount) && amount >= currencyConfig.min && amount <= currencyConfig.max;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (method !== 'online' || !amountIsValid || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const response = await fetch('/api/create-donation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, frequency, currency, comment: comment.trim() || undefined }),
      });
      const data = await response.json();
      if (!response.ok || !data.checkoutUrl) {
        throw new Error(data.error || 'Checkout failed');
      }
      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.error(err);
      setError(t.errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-8">
      {status === 'success' && (
        <div className="rounded-md border border-green-300 bg-green-50 p-4 text-sm font-medium text-green-800 dark:border-green-600 dark:bg-green-900/40 dark:text-green-200">
          {t.successMessage}
          {receiptUrl && (
            <a
              href={receiptUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 block font-semibold underline underline-offset-4 hover:text-green-900 dark:hover:text-green-100"
            >
              {t.receiptLink} ↗
            </a>
          )}
        </div>
      )}
      {status === 'cancelled' && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-4 text-sm font-medium text-yellow-800 dark:border-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-200">
          {t.cancelledMessage}
        </div>
      )}

      <fieldset>
        <legend className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">{t.methodLabel}</legend>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {(['online', 'transfer'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handleMethodChange(value)}
              className={presetButtonCSS(method === value)}
              aria-pressed={method === value}
            >
              {value === 'online' ? t.payOnline : t.bankTransfer}
            </button>
          ))}
        </div>
      </fieldset>

      {method === 'transfer' && (
        <div className="rounded-lg bg-gray-50 p-5 ring-1 ring-gray-200 dark:bg-white/5 dark:ring-white/10">
          <dl className="space-y-4 text-sm leading-6">
            {[
              { label: t.transfer.beneficiary, value: 'ASOCIAȚIA CREȘTINĂ KOINONIA 29' },
              { label: t.transfer.address, value: 'str. Cuza Vodă, nr. 85, Oradea' },
              { label: t.transfer.postalCode, value: '410097' },
              { label: 'IBAN', value: TRANSFER_IBAN, display: TRANSFER_IBAN_DISPLAY, mono: true },
              { label: t.transfer.bank, value: 'ING Bank' },
              { label: 'CIF', value: '54060230' },
            ].map(({ label, value, display, mono }) => (
              <div key={label}>
                <dt className="font-semibold text-gray-900 dark:text-white">{label}</dt>
                <dd
                  className={classNames('flex items-center gap-4 text-gray-600 dark:text-gray-300', {
                    'font-mono': mono,
                  })}
                >
                  {display ?? value}
                  <CopyTextClient global={global}>{value}</CopyTextClient>
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-5 text-xs leading-5 text-gray-500 dark:text-gray-400">{t.transfer.note}</p>
        </div>
      )}

      {method === 'online' && (
        <fieldset>
          <legend className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">{t.frequencyLabel}</legend>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {(['once', 'monthly'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setFrequency(value)}
                className={presetButtonCSS(frequency === value)}
                aria-pressed={frequency === value}
              >
                {value === 'once' ? t.once : t.monthly}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {method === 'online' && (
        <fieldset>
          <legend className="flex w-full items-baseline gap-3 text-sm font-semibold leading-6 text-gray-900 dark:text-white">
            {t.amountLabel}
            <Listbox value={currency} onChange={handleCurrencyChange}>
              <div className="relative">
                <Listbox.Button
                  aria-label={t.currencyLabel}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900 underline decoration-gray-400 decoration-dotted underline-offset-4 transition-colors hover:decoration-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600 dark:text-white dark:decoration-white/40 dark:hover:decoration-white/70"
                >
                  {currencyConfig.label}
                  <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                </Listbox.Button>
                <Listbox.Options className="absolute left-0 z-10 mt-2 w-28 overflow-hidden rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-800 dark:ring-white/10">
                  {(Object.keys(DONATION_CURRENCIES) as DonationCurrency[]).map((key) => (
                    <Listbox.Option
                      key={key}
                      value={key}
                      className={({ active, selected }) =>
                        classNames(
                          'cursor-pointer px-3 py-1.5 text-sm',
                          active && 'bg-gray-100 dark:bg-white/10',
                          selected ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300',
                        )
                      }
                    >
                      {DONATION_CURRENCIES[key].label}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </legend>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {currencyConfig.presets.map((preset) => (
              <button
                key={`${currency}-${preset}`}
                type="button"
                onClick={() => setSelectedPreset(preset)}
                className={presetButtonCSS(selectedPreset === preset)}
                aria-pressed={selectedPreset === preset}
              >
                {preset} {currencyConfig.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSelectedPreset('custom')}
              className={presetButtonCSS(selectedPreset === 'custom')}
              aria-pressed={selectedPreset === 'custom'}
            >
              {t.customAmount}
            </button>
          </div>
          {selectedPreset === 'custom' && (
            <div className="mt-3 flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min={currencyConfig.min}
                max={currencyConfig.max}
                step={1}
                value={customAmount}
                onChange={(event) => setCustomAmount(event.target.value)}
                autoFocus
                className="block w-40 rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-accent-600 dark:bg-white/5 dark:text-white dark:ring-white/10"
                aria-label={t.amountLabel}
              />
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{currencyConfig.label}</span>
            </div>
          )}
        </fieldset>
      )}

      {method === 'online' && (
        <div>
          <label htmlFor="donation-comment" className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
            {t.commentLabel}
          </label>
          <textarea
            id="donation-comment"
            rows={2}
            maxLength={DONATION_COMMENT_MAX_LENGTH}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            className="mt-3 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-accent-600 dark:bg-white/5 dark:text-white dark:ring-white/10"
          />
          <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">{t.commentNote}</p>
        </div>
      )}

      {method === 'online' && error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm font-medium text-red-700 dark:border-red-500 dark:bg-red-900/40 dark:text-red-200">
          {error}
        </div>
      )}

      {method === 'online' && (
        <div className="flex flex-col gap-3">
          <Button type="submit" disabled={!amountIsValid || isSubmitting} className="justify-center">
            {isSubmitting ? '…' : `${t.donateButton}${amountIsValid ? ` — ${amount} ${currencyConfig.label}` : ''}`}
          </Button>
          <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{t.redirectNote}</p>
          {frequency === 'monthly' && (
            <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{t.monthlyNote}</p>
          )}
        </div>
      )}
    </form>
  );
};
